// Serializes a TipTap document (editor.getJSON()) to markdown, so WYSIWYG
// editors can store the same markdown the rest of the app renders with
// components/Markdown. Covers the nodes/marks MarkdownEditor allows:
// paragraphs, headings, bullet/ordered lists, blockquotes, hard breaks,
// bold, italic, strike, inline code and links.
import type { JSONContent } from "@tiptap/react";

type Mark = NonNullable<JSONContent["marks"]>[number];

// Characters with inline markdown meaning (escaped in plain text).
const INLINE_SPECIAL = /([\\`*_~[\]])/g;

function escapeText(text: string): string {
  return text.replace(INLINE_SPECIAL, "\\$1");
}

/** Escapes things at the start of a line that markdown would read as block syntax. */
function escapeLineStart(line: string): string {
  return line
    .replace(/^(\s*)(#{1,6}\s)/, "$1\\$2")
    .replace(/^(\s*)([>+-]\s)/, "$1\\$2")
    .replace(/^(\s*)(\d+)\.(\s)/, "$1$2\\.$3");
}

const markKey = (marks: Mark[] = []) =>
  marks
    .map((m) => (m.type === "link" ? `link:${m.attrs?.href}` : m.type))
    .sort()
    .join("|");

/** Inline content → markdown. Adjacent text with identical marks is merged first. */
function inline(content: JSONContent[] = []): string {
  const runs: { text: string; marks: Mark[] }[] = [];
  for (const node of content) {
    if (node.type === "hardBreak") {
      runs.push({ text: "\n", marks: [] });
      continue;
    }
    if (node.type !== "text" || !node.text) continue;
    const last = runs[runs.length - 1];
    if (last && last.text !== "\n" && markKey(last.marks) === markKey(node.marks)) {
      last.text += node.text;
    } else {
      runs.push({ text: node.text, marks: node.marks ?? [] });
    }
  }

  return runs
    .map(({ text, marks }) => {
      if (text === "\n") return "  \n"; // markdown hard break
      const has = (t: string) => marks.some((m) => m.type === t);
      if (has("code")) {
        let out = "`" + text.replace(/`/g, "ˋ") + "`";
        const link = marks.find((m) => m.type === "link");
        if (link) out = `[${out}](${link.attrs?.href})`;
        return out;
      }
      // Keep surrounding whitespace outside the markers (`** bold**` isn't bold).
      const [, lead, core, trail] = text.match(/^(\s*)([\s\S]*?)(\s*)$/)!;
      if (!core) return text;
      let out = escapeText(core);
      if (has("strike")) out = `~~${out}~~`;
      if (has("italic")) out = `_${out}_`;
      if (has("bold")) out = `**${out}**`;
      const link = marks.find((m) => m.type === "link");
      if (link) out = `[${out}](${link.attrs?.href})`;
      return lead + out + trail;
    })
    .join("");
}

function indent(text: string, prefix: string): string {
  return text
    .split("\n")
    .map((line, i) => (i === 0 ? line : line ? prefix.replace(/./g, " ") + line : line))
    .join("\n");
}

function block(node: JSONContent): string {
  switch (node.type) {
    case "paragraph":
      return inline(node.content)
        .split("\n")
        .map(escapeLineStart)
        .join("\n");
    case "heading":
      return `${"#".repeat(node.attrs?.level ?? 2)} ${inline(node.content)}`;
    case "blockquote":
      return blocks(node.content)
        .split("\n")
        .map((l) => (l ? `> ${l}` : ">"))
        .join("\n");
    case "bulletList":
      return (node.content ?? []).map((item) => `- ${indent(blocks(item.content), "- ")}`).join("\n");
    case "orderedList": {
      const start = node.attrs?.start ?? 1;
      return (node.content ?? [])
        .map((item, i) => {
          const prefix = `${start + i}. `;
          return prefix + indent(blocks(item.content), prefix);
        })
        .join("\n");
    }
    default:
      return node.content ? blocks(node.content) : "";
  }
}

function blocks(content: JSONContent[] = []): string {
  return content
    .map(block)
    .filter((b) => b.trim() !== "")
    .join("\n\n");
}

export function editorJsonToMarkdown(doc: JSONContent): string {
  return blocks(doc.content).trim();
}
